import { Card, Typography, Box, Stack, useTheme } from '@mui/joy'

interface WeatherWarning {
    condition: string
    location: string
    temp?: number
    icon: string
}

interface Props {
    weatherWarnings: WeatherWarning[]
}

const WeatherIcon = ({ condition, icon }: { condition: string; icon: string }) => {
    const getAnimatedIcon = () => {
        if (condition.includes('rain') || condition.includes('drizzle')) {
            return (
                <Box sx={{ position: 'relative', width: 28, height: 28 }}>
                    <Box
                        sx={{
                            fontSize: '20px',
                            animation: 'weatherBounce 2s ease-in-out infinite',
                            '@keyframes weatherBounce': {
                                '0%, 100%': { transform: 'translateY(0px)' },
                                '50%': { transform: 'translateY(-3px)' },
                            },
                        }}
                    >
                        🌧️
                    </Box>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: '8px',
                            animation: 'weatherRain 1s linear infinite',
                            '@keyframes weatherRain': {
                                '0%': { opacity: 1, transform: 'translateX(-50%) translateY(0px)' },
                                '100%': { opacity: 0, transform: 'translateX(-50%) translateY(10px)' },
                            },
                        }}
                    >
                        💧💧💧
                    </Box>
                </Box>
            )
        }

        if (condition.includes('sun') || condition.includes('clear')) {
            return (
                <Box
                    sx={{
                        fontSize: '20px',
                        animation: 'weatherRotate 4s linear infinite',
                        '@keyframes weatherRotate': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                        },
                    }}
                >
                    ☀️
                </Box>
            )
        }

        if (condition.includes('cloud')) {
            return (
                <Box
                    sx={{
                        fontSize: '20px',
                        animation: 'weatherFloat 3s ease-in-out infinite',
                        '@keyframes weatherFloat': {
                            '0%, 100%': { transform: 'translateX(0px)' },
                            '50%': { transform: 'translateX(3px)' },
                        },
                    }}
                >
                    ☁️
                </Box>
            )
        }

        if (condition.includes('snow') || condition.includes('freezing')) {
            return (
                <Box sx={{ position: 'relative', width: 28, height: 28 }}>
                    <Box
                        sx={{
                            fontSize: '20px',
                            animation: 'weatherPulse 2s ease-in-out infinite',
                            '@keyframes weatherPulse': {
                                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                                '50%': { opacity: 0.7, transform: 'scale(1.05)' },
                            },
                        }}
                    >
                        ❄️
                    </Box>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: '6px',
                            animation: 'weatherSnow 2s linear infinite',
                            '@keyframes weatherSnow': {
                                '0%': { opacity: 1, transform: 'translateX(-50%) translateY(0px) rotate(0deg)' },
                                '100%': { opacity: 0, transform: 'translateX(-50%) translateY(12px) rotate(180deg)' },
                            },
                        }}
                    >
                        ❄️❄️❄️
                    </Box>
                </Box>
            )
        }

        if (condition.includes('storm') || condition.includes('thunder')) {
            return (
                <Box
                    sx={{
                        fontSize: '20px',
                        animation: 'weatherFlash 1.5s ease-in-out infinite',
                        '@keyframes weatherFlash': {
                            '0%, 90%, 100%': { opacity: 1 },
                            '10%, 80%': { opacity: 0.3 },
                            '20%, 70%': { opacity: 1 },
                            '30%, 60%': { opacity: 0.5 },
                        },
                    }}
                >
                    ⛈️
                </Box>
            )
        }

        if (condition.includes('wind')) {
            return (
                <Box
                    sx={{
                        fontSize: '20px',
                        animation: 'sway 1s ease-in-out infinite alternate',
                        '@keyframes sway': {
                            '0%': { transform: 'rotate(-5deg)' },
                            '100%': { transform: 'rotate(5deg)' },
                        },
                    }}
                >
                    💨
                </Box>
            )
        }

        // Default fallback
        return (
            <img
                src={`https://openweathermap.org/img/wn/${icon}@2x.png`}
                alt={condition}
                style={{
                    width: 24,
                    height: 24,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                }}
            />
        )
    }

    return (
        <Box
            sx={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            {getAnimatedIcon()}
        </Box>
    )
}

export const WeatherCard: React.FC<Props> = ({ weatherWarnings }) => {
    const theme = useTheme()
    const isDarkMode = theme.palette.mode === 'dark'

    if (!weatherWarnings || weatherWarnings.length === 0) {
        return null
    }

    return (
        <Card
            variant="plain"
            sx={{
                p: 1.2,
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
            }}
        >
            <Typography
                level="body-xs"
                sx={{
                    color: 'primary.400',
                    mb: 1,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                }}
            >
                ⚠️ Weather Alerts
            </Typography>

            <Stack spacing={0.8}>
                {weatherWarnings.map((warning, index) => (
                    <Box
                        key={index}
                        sx={{
                            p: 1,
                            borderRadius: '8px',
                            background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                            border: '1px solid',
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                background: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)',
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.15)',
                                transform: 'translateY(-2px)',
                                boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                            },
                        }}
                    >
                        <WeatherIcon condition={warning.condition} icon={warning.icon} />

                        <Box sx={{ flex: 1 }}>
                            <Typography
                                level="body-sm"
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    lineHeight: 1.3,
                                }}
                            >
                                {warning.condition === 'freezing'
                                    ? `🧊 Freezing in ${warning.location}`
                                    : `${warning.condition.charAt(0).toUpperCase() + warning.condition.slice(1)} in ${warning.location}`}
                            </Typography>
                            {warning.temp !== undefined && (
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                        mt: 0.2,
                                    }}
                                >
                                    🌡️ {warning.temp}°C
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
            </Stack>
        </Card>
    )
}
